/**
 * CompactTaskRow Component
 *
 * Compact row for displaying a task with status dot, title, date, and priority badge.
 * Uses the same UI components as the main TaskListRow for visual consistency.
 *
 * @module pages/DashboardPage/components/CompactTaskRow
 */

import type { CSSProperties } from 'react';
import { memo } from 'react';

import { STATUS_COLORS } from '@/types';
import { useLocalizedDate } from '@/hooks';
import { StatusBadge } from '@/components/common/StatusBadge';
import { PriorityBadge } from '@/components/ui/PriorityBadge';
import { UserAvatar } from '@/components/user/UserAvatar';

import type { CompactTaskRowProps } from './CompactTaskRow.types';
import styles from './CompactTaskRow.module.css';

export const CompactTaskRow = memo(function CompactTaskRow({
  task,
}: CompactTaskRowProps) {
  const { formatDateCompact } = useLocalizedDate();

  const dotStyle = {
    '--dot-color': STATUS_COLORS[task.status],
  } as CSSProperties;

  return (
    <div className={styles.row}>
      <span className={styles.dot} style={dotStyle} />
      <span className={styles.title}>{task.title}</span>
      {task.assigneeName ? (
        <div className={styles.assignee}>
          <UserAvatar
            displayName={task.assigneeName}
            avatarUrl={task.assigneeAvatarUrl}
            size="sm"
          />
          <span className={styles.assigneeName}>{task.assigneeName}</span>
        </div>
      ) : (
        <div className={styles.assignee}>
          <span className={styles.emDash}>&mdash;</span>
        </div>
      )}
      <span className={styles.priorityCol}>
        <PriorityBadge priority={task.priority} variant="inline" />
      </span>
      <span className={styles.status}>
        <StatusBadge type="taskStatus" value={task.status} size="sm" />
      </span>
      <span className={styles.date}>
        {task.dueDate ? formatDateCompact(task.dueDate) : '\u2014'}
      </span>
    </div>
  );
});

CompactTaskRow.displayName = 'CompactTaskRow';
